import type { Configuration, OnError, OnNudge, Storage } from './contracts';
import ErrorWithContext from './ErrorWithContext';
import { safeParse, toError } from './utilities';
import Result, { Reason } from '../Result';

const version = 1;
const fifteenMinutes = 15 * 60 * 1000;
const fourHours = 4 * 60 * 60 * 1000;

function wasShownRecently(time: Date, cooldown: number): boolean {
  return new Date().getTime() - time.getTime() < cooldown;
}

function isValidStorage(v: unknown): v is Storage {
  return (
    typeof v === 'object' &&
    v != null &&
    'lastShown' in v &&
    (typeof v.lastShown === 'string' || v.lastShown == null) &&
    'nudgesLastShown' in v &&
    typeof v.nudgesLastShown === 'object' &&
    v.nudgesLastShown != null &&
    'version' in v &&
    typeof v.version === 'number'
  );
}

export default class NudgeManager {
  isModalOpen = false;

  private _configuration: Configuration;

  constructor(
    configuration: Partial<Configuration>,
    private onNudge?: OnNudge,
    private onError?: OnError
  ) {
    this._configuration = {
      globalCooldownMs: fifteenMinutes,
      nudgeCooldownMs: fourHours,
      storageKey: 'merch-nudges',
      ...configuration
    };
  }

  async mayNudgeShow(nudgeId: string): Promise<Result> {
    try {
      const result = await this.checkMayNudgeShow(nudgeId);

      this.onNudge?.call(null, nudgeId, result);

      return result;
    } catch (err) {
      const error = toError(err);

      this.onError?.call(null, nudgeId, error);

      return new Result(false, Reason.Error, error);
    }
  }

  initializeStorage(storage: Storage): void {
    this.setStorage(storage);
  }

  clearStorage(): void {
    globalThis.localStorage.removeItem(this._configuration.storageKey);
  }

  private async checkMayNudgeShow(nudgeId: string): Promise<Result> {
    if (this.checkModalOpen()) {
      return new Result(false, Reason.ModalOpen);
    }

    const nudgeShownRecently = this.checkNudgeShownRecently(nudgeId);

    if (!nudgeShownRecently.mayShow) {
      return nudgeShownRecently;
    }

    this.setNudgeShown(nudgeId);

    return new Result(true);
  }

  private checkModalOpen(): boolean {
    return this.isModalOpen || (globalThis.merch?.isModalOpen ?? false);
  }

  private checkNudgeShownRecently(nudgeId: string): Result {
    const storage = this.getStorage();

    if (!storage.lastShown) {
      return new Result(true);
    }

    if (
      wasShownRecently(storage.lastShown, this._configuration.globalCooldownMs)
    ) {
      return new Result(false, Reason.ShownRecently);
    }

    const nudgeLastShown = storage.nudgesLastShown[nudgeId];
    const nudgeShownRecently = nudgeLastShown
      ? wasShownRecently(nudgeLastShown, this._configuration.nudgeCooldownMs)
      : false;

    return nudgeShownRecently
      ? new Result(false, Reason.NudgeShownRecently)
      : new Result(true);
  }

  private setNudgeShown(nudgeId: string): void {
    const storage = this.getStorage();

    storage.lastShown = new Date();
    storage.nudgesLastShown[nudgeId] = new Date();
    storage.version = version;

    this.setStorage(storage);
  }

  private getStorage(): Storage {
    const defaultStorage: Storage = {
      version,
      lastShown: null,
      nudgesLastShown: {}
    };

    const storageString = globalThis.localStorage.getItem(
      this._configuration.storageKey
    );

    const storage = storageString
      ? safeParse<Storage>(storageString)
      : defaultStorage;

    if (storage == null || !isValidStorage(storage)) {
      this.clearStorage();

      throw new ErrorWithContext('Invalid NudgeManager storage', {
        storage,
        storageString
      });
    }

    storage.lastShown = storage.lastShown ? new Date(storage.lastShown) : null;

    Object.keys(storage.nudgesLastShown).forEach(key => {
      storage.nudgesLastShown[key] = new Date(storage.nudgesLastShown[key]);
    });

    return storage;
  }

  private setStorage(storage: Storage): void {
    const storageString = JSON.stringify({
      ...storage,
      lastShown: storage.lastShown?.toISOString() ?? null,
      nudgesLastShown: Object.fromEntries(
        Object.entries(storage.nudgesLastShown).map(([key, value]) => [
          key,
          value.toISOString()
        ])
      )
    });

    globalThis.localStorage.setItem(
      this._configuration.storageKey,
      storageString
    );
  }
}
